import React, { useState, Component } from 'react';
import { MapPin, Phone, Clock, Mail, CheckCircle, Send } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslations } from '../hooks/useTranslations';
export function Contact() {
  const {
    t
  } = useTranslations();
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    plan: '',
    message: '',
    termsConsent: false,
    smsConsent: false
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.termsConsent || !formData.smsConsent) {
      return;
    }
    setFormState('submitting');
    setTimeout(() => {
      // Prepare WhatsApp message
      const message = `Nueva Solicitud de Brixen Credit:

Nombre: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Teléfono: ${formData.phone}
Plan: ${formData.plan}
Mensaje: ${formData.message || 'N/A'}

Consentimientos:
✓ Términos y Condiciones aceptados
✓ Consentimiento SMS aceptado`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappNumber = '17866279073'; // 786-627-9073 in international format
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
      // Redirect to WhatsApp
      window.location.href = whatsappUrl;
    }, 1000);
  };
  const resetForm = () => {
    setFormState('idle');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      plan: '',
      message: '',
      termsConsent: false,
      smsConsent: false
    });
  };
  const openLegalModal = (type: 'privacy' | 'terms') => {
    window.dispatchEvent(new CustomEvent('openLegalModal', {
      detail: type
    }));
  };
  const isFormValid = formData.termsConsent && formData.smsConsent;
  return <section id="contact" className="py-20 bg-gray-50" data-id="element-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-18">
          {/* Left Side - Info */}
          <div data-id="element-19">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-20">
              {t('contact.title')}
            </h2>
            <p className="text-lg text-gray-600 mb-10" data-id="element-21">
              {t('contact.subtitle')}
            </p>

            <div className="space-y-6" data-id="element-22">
              <div className="flex items-start" data-id="element-23">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-24">
                  <MapPin className="w-6 h-6" data-id="element-25" />
                </div>
                <div className="ml-4" data-id="element-26">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-27">
                    {t('contact.address')}
                  </h3>
                  <p className="text-gray-600" data-id="element-28">
                    390 NE 191ST ST STE 8123
                    <br data-id="element-29" />
                    Miami, FL 33179
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-30">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-31">
                  <Phone className="w-6 h-6" data-id="element-32" />
                </div>
                <div className="ml-4" data-id="element-33">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-34">
                    {t('contact.phone')}
                  </h3>
                  <p className="text-gray-600" data-id="element-35">786-627-9073</p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-36">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-37">
                  <Mail className="w-6 h-6" data-id="element-38" />
                </div>
                <div className="ml-4" data-id="element-39">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-40">
                    {t('contact.email')}
                  </h3>
                  <p className="text-gray-600" data-id="element-41">soporte@brixencredit.com</p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-42">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-43">
                  <Clock className="w-6 h-6" data-id="element-44" />
                </div>
                <div className="ml-4" data-id="element-45">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-46">
                    {t('contact.hours')}
                  </h3>
                  <p className="text-gray-600" data-id="element-47">{t('contact.hoursValue')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100" data-id="element-48">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-49">
              {t('contact.formTitle')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-51">
                <div data-id="element-52">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-53">
                    {t('contact.firstName')} *
                  </label>
                  <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" placeholder="Juan" data-id="element-54" />
                </div>
                <div data-id="element-55">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-56">
                    {t('contact.lastName')} *
                  </label>
                  <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" placeholder="Pérez" data-id="element-57" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-58">
                <div data-id="element-59">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-60">
                    {t('contact.emailLabel')} *
                  </label>
                  <input type="email" id="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" placeholder="juan@email.com" data-id="element-61" />
                </div>
                <div data-id="element-62">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-63">
                    {t('contact.phoneLabel')} *
                  </label>
                  <input type="tel" id="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" placeholder="(555) 123-4567" data-id="element-64" />
                </div>
              </div>

              <div data-id="element-65">
                <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-66">
                  {t('contact.plan')} *
                </label>
                <select id="plan" value={formData.plan} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white" data-id="element-67">
                  <option value="" data-id="element-68">{t('contact.planPlaceholder')}</option>
                  <option value="Plan Gold - $119/mes" data-id="element-69">
                    Plan Gold - $119/mes
                  </option>
                  <option value="Plan Platinum - $149/mes" data-id="element-70">
                    Plan Platinum - $149/mes
                  </option>
                  <option value="Plan Express VIP - $1,499" data-id="element-71">
                    Plan Express VIP - $1,499
                  </option>
                  <option value="Plan Express VIP+ - $1,999" data-id="element-72">
                    Plan Express VIP+ - $1,999
                  </option>
                </select>
              </div>

              <div data-id="element-73">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-74">
                  {t('contact.message')}
                </label>
                <textarea id="message" value={formData.message} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none" placeholder={t('contact.messagePlaceholder')} data-id="element-75" />
              </div>

              {/* Terms & SMS Consent Section */}
              <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-76">
                <h4 className="text-lg font-bold text-gray-900 mb-4" data-id="element-77">
                  Terms & SMS Consent
                </h4>

                {/* Terms Consent */}
                <div className="mb-4" data-id="element-78">
                  <label className="flex items-start group cursor-pointer" data-id="element-79">
                    <input type="checkbox" checked={formData.termsConsent} onChange={e => setFormData({
                    ...formData,
                    termsConsent: e.target.checked
                  })} className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer" required data-id="element-80" />
                    <span className="ml-3 text-sm text-gray-700 leading-relaxed" data-id="element-81">
                      I agree to the{' '}
                      <button type="button" onClick={() => openLegalModal('terms')} className="text-purple-600 hover:underline font-medium" data-id="element-82">
                        Terms and Conditions
                      </button>{' '}
                      and{' '}
                      <button type="button" onClick={() => openLegalModal('privacy')} className="text-purple-600 hover:underline font-medium" data-id="element-83">
                        Privacy Policy
                      </button>
                      .
                    </span>
                  </label>
                </div>

                {/* SMS Consent Disclosure */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200" data-id="element-84">
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-85">
                    BRIXEN CREDIT would like your consent to send text message
                    communications from +1 (786) 627-9073 to your mobile number
                    listed above, regarding customer care. Consent is not a
                    condition of purchase. Message frequency varies. Message and
                    data rates may apply. Reply STOP to unsubscribe at any time.
                    Reply HELP for assistance or more information.
                  </p>
                </div>

                {/* SMS Consent Checkbox */}
                <div className="mb-4" data-id="element-86">
                  <label className="flex items-start group cursor-pointer" data-id="element-87">
                    <input type="checkbox" checked={formData.smsConsent} onChange={e => setFormData({
                    ...formData,
                    smsConsent: e.target.checked
                  })} className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer" required data-id="element-88" />
                    <span className="ml-3 text-sm text-gray-700 leading-relaxed" data-id="element-89">
                      Yes, I consent to receive informational messages from
                      BRIXEN CREDIT regarding customer care.
                    </span>
                  </label>
                </div>

                {/* Privacy Note */}
                <div className="bg-purple-50 border border-purple-100 p-3 rounded-lg" data-id="element-90">
                  <p className="text-xs text-gray-600" data-id="element-91">
                    We do not share your mobile opt-in information with anyone.
                    See our{' '}
                    <button type="button" onClick={() => openLegalModal('privacy')} className="text-purple-600 hover:underline font-medium" data-id="element-92">
                      Privacy Policy
                    </button>{' '}
                    for more information on how we handle your data.
                  </p>
                </div>
              </div>

              <Button type="submit" fullWidth size="lg" isLoading={formState === 'submitting'} disabled={!isFormValid} className={`mt-4 ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-93">
                <Send className="w-5 h-5 mr-2" data-id="element-94" />
                Request Call Back
              </Button>

              {!isFormValid && <p className="text-xs text-center text-gray-500 mt-2" data-id="element-95">
                  Please accept both consent agreements to continue
                </p>}
            </form>
          </div>
        </div>
      </div>
    </section>;
}